import { Cake, Celebration, EmojiEvents } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React from 'react';
import { Link } from 'react-router-dom';
import GoogleSignInButton from '../components/GoogleSignInButton.tsx';

// Animation variants for elements
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      when: "beforeChildren", 
      staggerChildren: 0.3,
      duration: 0.5
    } 
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Custom animation variants for icons
const floatingVariants = {
  float: {
    y: [0, -5, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      repeatType: "mirror" as const,
      ease: "easeInOut"
    }
  }
};

const pulsingVariants = {
  pulse: {
    scale: [0.9, 1.1, 0.9],
    transition: { 
      duration: 3,
      repeat: Infinity,
      repeatType: "mirror" as const
    }
  }
};

const waveVariants = {
  wave: {
    rotate: [0, 5, -5, 0],
    transition: { 
      duration: 2.5, 
      repeat: Infinity,
      repeatType: "mirror" as const,
      ease: "easeInOut"
    }
  }
};

// Button hover animations
const buttonHoverVariants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.05,
    transition: { type: "spring", stiffness: 400 }
  },
  tap: { scale: 0.95 }
};

const LandingPage: React.FC = () => {
  const theme = useTheme();
  
  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50"
    >
      {/* Navigation */}
      <nav className="py-4 backdrop-blur-lg bg-white/70 sticky top-0 z-50 shadow-sm">
        <div className="flex justify-between items-center w-full max-w-7xl mx-auto px-4 xl:px-20">
          <Typography 
            variant="h6" 
            component={Link}
            to="/app"
            fontWeight="600"
            className="text-center whitespace-nowrap"
            sx={{ 
              display: 'flex', 
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              textDecoration: 'none',
              alignItems: 'center',
              color: 'inherit',
              '&:hover': {
                color: 'primary.main',
                cursor: 'pointer',
                transform: 'scale(1.02)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <Box 
              component={motion.span}
              animate={{ 
                rotate: [0, 10, -10, 0],
                transition: { repeat: Infinity, duration: 5, repeatType: 'loop' }
              }}
              sx={{ mr: 1 }}
            >
              <img src="/apple-icon-180x180.png" className="w-10 h-10" alt="Logo" />
            </Box>
            Birthday Planner
          </Typography>
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonHoverVariants}
          >
            <Link to="/app" className="text-center px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all shadow-md">
              Try it now!
            </Link>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-28 text-center relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-1/4 right-[10%] w-40 h-40 rounded-full bg-yellow-100 blur-3xl opacity-70 -z-10"></div>
        <div className="absolute bottom-1/4 left-[5%] w-64 h-64 rounded-full bg-blue-100 blur-3xl opacity-70 -z-10"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10"
        >
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center space-x-3">
              <motion.div variants={floatingVariants} animate="float">
                <Cake sx={{ color: theme.palette.primary.main, fontSize: 48 }} />
              </motion.div>
              <motion.div variants={pulsingVariants} animate="pulse">
                <Celebration sx={{ color: '#FFD54F', fontSize: 48 }} />
              </motion.div>
              <motion.div variants={waveVariants} animate="wave">
                <EmojiEvents sx={{ color: '#FF8A65', fontSize: 48 }} />
              </motion.div>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold pb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-400">
            Make Every Birthday Special
          </h1>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Never forget another important date, and stay ahead with saved gift ideas.
          </p>
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonHoverVariants}
            className="inline-block"
          >
            <Link to="/app" className="px-8 py-4 bg-blue-600 text-white rounded-xl text-lg font-medium hover:bg-blue-700 transition-all shadow-lg inline-flex items-center space-x-2">
              <span>Open the App!</span>
              <span className="ml-1">→</span>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-white to-blue-50 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-16"
          >
            Our Features
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "🎂",
                color: "rgba(36, 100, 235, 0.1)",
                title: "Unlimited Birthdays",
                description: "Never miss another birthday with reminders that work for you."
              },
              {
                icon: "📅",
                color: "rgba(144, 112, 255, 0.1)",
                title: "Calendar Integration",
                description: "Seamlessly sync with your calendar apps to keep all your important dates in one place."
              },
              {
                icon: "🎁",
                color: "rgba(255, 123, 123, 0.1)",
                title: "Gift Ideas Manager",
                description: "Keep track of gift ideas year-round and never struggle with last-minute present shopping."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="flex flex-col items-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
              >
                <div className="text-4xl mb-6 w-20 h-20 rounded-full flex items-center justify-center" style={{ background: feature.color }}>
                  {feature.icon}
                </div>
                <h3 className="text-center text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-center text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-16"
          >
            How It Works
          </motion.h2>
          <div className="relative">
            <div className="grid md:grid-cols-5 gap-8">
              {[
                { step: "1", title: "Get Started", desc: "Create your account" },
                { step: "2", title: "Add Birthdays", desc: "Input important dates" },
                { step: "3", title: "Save Gifts", desc: "Keep track of gift ideas" },
                { step: "4", title: "Get Reminders", desc: "Customized notifications" },
                { step: "5", title: "Celebrate 🎉", desc: "Make memories special" }
              ].map((item, index) => (
                <motion.div 
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="text-center relative"
                >
                  <motion.div 
                    whileHover={{ scale: 1.1, backgroundColor: '#2464eb', color: '#fff' }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold mx-auto mb-4 relative z-10"
                  >
                    {item.step}
                  </motion.div>
                  <h3 className="font-semibold mb-2 text-xl">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-blue-500 text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-blue-400 opacity-20"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-yellow-300 opacity-10"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to Get Started?</h2>
            <p className="text-xl max-w-2xl mx-auto">
              Our users never miss birthdays, and always bring the perfect gift.
            </p>
            <p className="text-xl mb-10 max-w-2xl mx-auto">
            Sign in to create your free account today!
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonHoverVariants}
              >
                <Link to="/app" className="px-8 py-4 bg-white text-blue-600 rounded-xl text-lg font-semibold hover:bg-blue-50 transform transition-all shadow-lg sm:w-auto w-full inline-flex items-center justify-center">
                  Get Started Now!
                </Link>
              </motion.div>
            </div>
            <div className="mt-6 sm:hidden block">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white px-4 py-2 rounded-xl shadow-lg inline-block"
              >
                <GoogleSignInButton size="large" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center text-gray-600">
            <div className="flex items-center justify-center mb-8">
              <Typography 
                variant="h6" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 600
                }}
              >
                <Box 
                  component={motion.span}
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    transition: { repeat: Infinity, duration: 5, repeatType: 'loop' }
                  }}
                  sx={{ mr: 1 }}
                >
                  <img src="/apple-icon-180x180.png" className="w-10 h-10" alt="Logo" />
                </Box>
                <Box component="span">
                  Birthday Planner
                </Box>
              </Typography>
            </div>
            <p className="mb-6">Making celebrations special, one birthday at a time.</p>
            <div className="space-x-6 mb-8">
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms of Service</Link>
            </div>
            <p className="text-sm">© 2025 Birthday Planner. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default LandingPage; 